import { ReactNode, useEffect, useState } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import { useNavigate } from '@plasma/ui.application.router';
import Notification from '@plasma/ui.feedback.notification';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { apiUriRfqActionHttp } from '../../services/conf/apiConf';
import { fetchAllDataGet_byApiName, fetchAllDataGet_byApiName_for_RFQ } from '../../services/gets/getdata';
import { sendAllFileByPost, sendAllDataPost_byApiNameAndData } from '../../services/posts/postdata';
import oneRFQDetailsColumns from '../../types/oneRFQDetailsDatatype';
import { CurrentRfqStatusProps } from './current-rfq-status';
import help from './current-rfq-status.help';
import translations from './i18n';

function useCurrentRfqStatusHook(props: CurrentRfqStatusProps) {
  useResourceBundle(translations, undefined, '84f9a732-617d-4fb1-8d98-80a6d971ceb1');
  useHelpBundle(help);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [dataForColumns, setdataForColumns] = useState([]);
  const [dataForRFQ, setdataForRFQ] = useState([]);
  const [columnsForRFQ, setcolumnsForRFQ] = useState(oneRFQDetailsColumns());
  const [formattedData, setFormattedData] = useState<Record<string, ReactNode>[]>([]);
  const [modalVisible, setmodalVisible] = useState(false);
  const [showRFQModal, setshowRFQModal] = useState(false);
  const [superRole, setSuperRole] = useState(null);
  const [statusCodes, setStatusCodes] = useState([]);
  const [reportName, setreportName] = useState('');
  const [pdfBlob, setPdfBlob] = useState<string>('');
  const [iFrameSrc, setIframeSrc] = useState('');
  const [pageLoading, setPageLoading] = useState<boolean>(false);

  useEffect(() => {
    getStatusCode();
    fetchTableData();
  }, [superRole]);

  const fetchTableData = async () => {
    setPageLoading(true);
    fetchAllDataGet_byApiName('getAllRFQ', apiUriRfqActionHttp())
      .then((data) => {
        setdataForColumns(data);
      })
      .catch((error) => {
        console.log('Failed to Fetch companies');
      })
      .finally(() => {
        setPageLoading(false);
      });

    if (!superRole) {
      const storedData = sessionStorage.getItem('superUserRole');
      if (storedData) {
        setSuperRole(storedData);
      }
    }
  };

  const getStatusCode = async () => {
    if (superRole === 'Ts-admin') {
      fetchAllDataGet_byApiName(`GetStatusCodes?topic=RFQTS`, apiUriRfqActionHttp())
        .then((responseTS) => {
          const statusCodesWithColors = responseTS.map((ts) => ({
            statuscode: ts.status,
            colorcode: ts.colorCode,
          }));
          setStatusCodes(statusCodesWithColors);
        })
        .catch((error) => {
          console.log('Failed to Fetch RfqTs');
        });
    } else {
      fetchAllDataGet_byApiName(`GetStatusCodes?topic=RFQCUS`, apiUriRfqActionHttp())
        .then((responseCus) => {})
        .catch((error) => {
          console.log('Failed to Fetch RfqTs');
        });
    }
  };

  useEffect(() => {
    if (dataForRFQ.length > 0) {
      setmodalVisible(true);
    }
  }, [dataForRFQ]);

  const handleRowSelection = (selectedRowIndex, selectedRowData, selectedEventData) => {
    setPageLoading(true);
    fetchAllDataGet_byApiName(
      `getAllRFQDetails?RFQCode=${selectedRowData.rfqcode.props.children}`,
      apiUriRfqActionHttp(),
    )
      .then((data) => {
        setdataForRFQ(data);
      })
      .catch((error) => {
        console.log('Failed to Fetch companies');
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const handleModalCancel = () => {
    setmodalVisible(false);
  };

  const handleRFQModalCancel = () => {
    setshowRFQModal(false);
  };

  useEffect(() => {
    if (iFrameSrc) {
      setshowRFQModal(true);
    }
  }, [iFrameSrc]);

  const handleModalViewRFQ = async () => {
    // Ensure we actually have data before calling the API
    if (!dataForRFQ || dataForRFQ.length === 0) {
      Notification.error('Error!', 'No RFQ data available to generate report.', undefined, 'medium', 2);
      return;
    }
    setPageLoading(true);
    Notification.info('', 'Waiting for RFQ File...', undefined, 'medium', 1);

    // Extract the identifier from your data state
    const currentRfqCode = dataForRFQ[0]?.rfqCode;

    // Append the API string
    const apiEndpoint = `GenerateRfqReport?rfqId=${currentRfqCode}`;

    await fetchAllDataGet_byApiName_for_RFQ(apiEndpoint, apiUriRfqActionHttp())
      .then(({ headers, data }) => {
        const contentDisposition = headers.get('Content-Disposition');
        let filename = 'default_filename.pdf';

        if (contentDisposition) {
          const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        setreportName(filename);
        setIframeSrc(data);
        setPdfBlob(data);
      })
      .catch((error) => {
        console.log('Failed to Fetch Data:', error);
        Notification.error('Error!', 'Unsuccessful Attempt', undefined, 'medium', 2);
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const base64toBlob = (base64Data: string): Blob => {
    const binaryString = window.atob(base64Data.split(',')[1]);
    const byteArray = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      byteArray[i] = binaryString.charCodeAt(i);
    }
    return new Blob([byteArray], { type: 'application/pdf' });
  };

  const handleRFQEmailSend = () => {
    setPageLoading(true);
    const rfqActionParam = {
      status: 'sendmail',
      rfqCode: reportName.replace('.pdf', '').replace(/-/g, '/'),
    };

    const rfqActionParamtToCancel = {
      status: 'cancel',
      rfqCode: reportName.replace('.pdf', '').replace(/-/g, '/'),
    };

    const blob = base64toBlob(pdfBlob);
    const formData = new FormData();
    formData.append('File', blob, reportName); // 'pdfFile' is the field name for the file
    formData.append('base64BinaryStr', '');

    sendAllFileByPost('sendEmail', formData)
      .then((response) => {
        if (response.code === '0') {
          Notification.success(response.message, response.responseData);
          sendAllDataPost_byApiNameAndData('HandleRfqAction', rfqActionParam, apiUriRfqActionHttp())
            .then((data) => {
              getStatusCode();
              fetchTableData();
              setIframeSrc('');
              setPageLoading(false);
              setshowRFQModal(false);
              setmodalVisible(false);
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          Notification.error(response.message, response.responseData);
          sendAllDataPost_byApiNameAndData('HandleRfqAction', rfqActionParamtToCancel, apiUriRfqActionHttp())
            .then((data) => {
              getStatusCode();
              fetchTableData();
              setIframeSrc('');
              setPageLoading(false);
              setshowRFQModal(false);
            })
            .catch((error) => {
              console.log(error);
            });
        }
      })
      .catch((error) => {
        console.log(error);
        setPageLoading(false);
        Notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 2);
      });
  };

  return {
    t,
    dataForColumns,
    formattedData,
    setFormattedData,
    dataForRFQ,
    columnsForRFQ,
    modalVisible,
    showRFQModal,
    handleRowSelection,
    handleModalCancel,
    handleModalViewRFQ,
    handleRFQModalCancel,
    handleRFQEmailSend,
    statusCodes,
    pageLoading,
    iFrameSrc,
    pdfBlob,
    reportName,
  };
}

export default useCurrentRfqStatusHook;
